/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { InitialScrsTable, NumericTable } from "../types"

/** A column of the initial-SCRs table that a finiteness guard can name. */
export type InitialScrsColumn = keyof InitialScrsTable

/**
 * Thrown when a value reaching these computations is not a finite number.
 * `InitialScrsTable` declares every column as plain numbers and the fetch layer
 * enforces it (services/fetching/initialScrs.ts, validateInitialScrsResponse), so
 * this signals a contract violation on some other data path - a cached table, a
 * fixture, a relaxed validation - rather than anything a user can provoke.
 */
export class NonFiniteColumnError extends Error {
  readonly column: InitialScrsColumn
  readonly index: number
  readonly value: unknown

  constructor(computation: string, column: InitialScrsColumn, index: number, value: unknown) {
    super(
      `${computation}: ${column}[${index}] is ${String(value)}, not a finite number. ` +
        `The ${column} column must contain only finite numbers.`
    )
    this.name = "NonFiniteColumnError"
    this.column = column
    this.index = index
    this.value = value
  }
}

/**
 * Rejects a column that violates its declared all-finite-numbers contract, before
 * any arithmetic or comparison can silently coerce the offending value.
 *
 * AIDEV-NOTE: issues #533 and #781. Every consumer degrades quietly rather than
 * loudly without this: `null - number` coerces the null to 0, so a null SCR sorts
 * as a zero and drags the low quantiles down, `null < rate` counts it as a
 * failure, `null >= min` buckets a null CAPE or drawdown wherever zero happens to
 * fall, and a null `c1`/`sum_of_ct`/`sum_of_ctpt` turns a portfolio's final value
 * into a different, plausible number. The backend serializer nulls *every* float
 * column it emits, not only `initial_scr`
 * (backend/swr_toolbox/models/initial_scrs_response.py), so nulls are what would
 * arrive in any of them. Throwing rather than skipping them the way the backend's
 * Polars aggregations do is deliberate - "Use Fallbacks Cautiously" in AGENTS.md -
 * since a silent skip hides the upstream data fault that produced the value.
 *
 * One O(n) pass per guarded column, and callers guard only the columns they read,
 * so this is a constant-factor addition to work already done over the same rows.
 *
 * `Number.isFinite` over the global `isFinite`: it does not coerce, so it rejects
 * `null`, `undefined` and strings as well as NaN and +/-Infinity.
 */
export function assertFiniteColumn(
  values: readonly number[],
  column: InitialScrsColumn,
  computation: string
): void {
  const badIndex = values.findIndex(value => !Number.isFinite(value))
  if (badIndex !== -1) {
    throw new NonFiniteColumnError(computation, column, badIndex, values[badIndex])
  }
}

/**
 * Finds the consumption rate at a specific failure rate percentile.
 *
 * @param filteredInitialScrs Filtered InitialScrsTable
 * @param failureRate The failure rate threshold (between 0 and 1)
 * @returns The consumption rate that corresponds to the given failure rate
 * @throws NonFiniteColumnError if any initial_scr value is not a finite number
 */
export function computeConsumptionRatesForSelectedCohorts(
  filteredInitialScrs: InitialScrsTable,
  failureRates: readonly number[]
): NumericTable {
  const result: NumericTable = {}

  assertFiniteColumn(
    filteredInitialScrs.initial_scr,
    "initial_scr",
    "computeConsumptionRatesForSelectedCohorts"
  )

  // Check if filteredInitialScrs has the required data
  if (filteredInitialScrs.initial_scr.length === 0) {
    // Return empty result for each failure rate
    failureRates.forEach(fr => {
      result[fr.toFixed(2)] = [NaN]
    })
    return result
  }

  const initialScrsArray = filteredInitialScrs.initial_scr

  // 1) Sort in ascending order
  const sorted = [...initialScrsArray].sort((a, b) => a - b)
  const n = sorted.length

  // 2) For each failure rate, compute idx = fr*(n-1), floor it, and linearly interpolate
  failureRates.forEach(fr => {
    const idx = fr * (n - 1)
    const lo = Math.floor(idx)
    const frac = idx - lo

    let quantile: number
    if (lo < 0) {
      quantile = sorted[0]
    } else if (lo >= n - 1) {
      quantile = sorted[n - 1]
    } else {
      quantile = sorted[lo] * (1 - frac) + sorted[lo + 1] * frac
    }

    // 3) Alias column name as string rounded to 2 decimals
    result[fr.toFixed(2)] = [quantile]
  })

  return result
}

/**
 * Calculates the failure rate for a given consumption rate.
 *
 * @param initialScrs Filtered InitialScrsTable
 * @param consumptionRate The consumption rate threshold
 * @returns The proportion of values less than the consumption rate (failure rate)
 * @throws NonFiniteColumnError if any initial_scr value is not a finite number
 */
export function computeFailureRatesForSelectedCohorts(
  initialScrs: InitialScrsTable,
  consumptionRates: readonly number[]
): NumericTable {
  const result: NumericTable = {}

  assertFiniteColumn(
    initialScrs.initial_scr,
    "initial_scr",
    "computeFailureRatesForSelectedCohorts"
  )

  // AIDEV-NOTE: NaN, not 0, is the "no data" sentinel here. A failure rate is
  // undefined over an empty cohort, and 0 is indistinguishable downstream from a
  // genuine perfect record - makeFailureRateAndConsumptionTables drops NaN
  // columns but would render zeros as a "0% failed" claim backed by no data.
  // Matches computeConsumptionRatesForSelectedCohorts above and the backend,
  // whose Polars aggregation over zero rows is null (issue #532).
  if (initialScrs.initial_scr.length === 0) {
    consumptionRates.forEach(cr => {
      result[String(cr)] = [NaN]
    })
    return result
  }

  const initialScrsArray = initialScrs.initial_scr

  const n = initialScrsArray.length

  consumptionRates.forEach(cr => {
    // Count values less than the consumption rate
    const failCount = initialScrsArray.filter(scr => scr < cr).length
    const failureRate = failCount / n

    // Use string representation to match Python's str(consumption_rate)
    result[String(cr)] = [failureRate]
  })

  return result
}
